import CryptoJS from "crypto-js";

// const ENCRYPTION_METHOD = CryptoJS.AES;
const IV_LENGTH = 16; // AES IV length in bytes
const KEY_LENGTH = 32; // AES key length in bytes
const CRC16_LENGTH = 4; // CRC-16 checksum length

/**
 * Encrypt data using AES and return the encrypted data as a combined string with CRC-16 checksum.
 */
const encryptWithCRC16 = (data, key) => {
  // Generate IV
  const iv = CryptoJS.lib.WordArray.random(IV_LENGTH);

  // Encrypt the data using AES-256-CBC and the provided key and IV
  const encryptedData = CryptoJS.AES.encrypt(
    data,
    CryptoJS.enc.Hex.parse(key),
    {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    }
  ).ciphertext;

  // Calculate CRC-16 checksum of the encrypted data
  const crc16 = calculateCRC16(encryptedData);

  // Convert CRC-16 to hexadecimal string
  const crc16Hex = crc16.toString(16).padStart(4, "0");

  // Combine IV, encrypted data, key, and CRC-16 into one string
  const result =
    iv.toString(CryptoJS.enc.Hex) +
    key +
    encryptedData.toString(CryptoJS.enc.Hex) +
    crc16Hex;
  return result;
};

/**
 * Decrypt data using AES and verify CRC-16 checksum.
 */
const decryptWithCRC16 = (encryptedString) => {
  const ivLengthHex = IV_LENGTH * 2;
  const keyLengthHex = KEY_LENGTH * 2;
  const crc16LengthHex = CRC16_LENGTH;

  if (encryptedString.length < ivLengthHex + keyLengthHex + crc16LengthHex) {
    throw new Error(
      "Encrypted string is too short to contain necessary components."
    );
  }

  // Extract components
  const ivHex = encryptedString.slice(0, ivLengthHex);
  const keyHex = encryptedString.slice(ivLengthHex, ivLengthHex + keyLengthHex);
  const dataHex = encryptedString.slice(
    ivLengthHex + keyLengthHex,
    -crc16LengthHex
  );
  const crc16Hex = encryptedString.slice(-crc16LengthHex);

  // Convert from hex to binary
  const iv = CryptoJS.enc.Hex.parse(ivHex);
  const key = keyHex;
  const encryptedData = CryptoJS.enc.Hex.parse(dataHex);
  const providedCrc16 = parseInt(crc16Hex, 16);

  // Verify CRC16 checksum
  const calculatedCrc16 = calculateCRC16(encryptedData);

  console.log("calculatedCrc16: ", calculatedCrc16);

  if (calculatedCrc16 !== providedCrc16) {
    throw new Error(
      "CRC-16 checksum does not match. Data integrity verification failed."
    );
  }

  // Decrypt the data using the provided key
  const decryptedData = CryptoJS.AES.decrypt(
    { ciphertext: encryptedData },
    CryptoJS.enc.Hex.parse(key),
    {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    }
  ).toString(CryptoJS.enc.Utf8);

  if (!decryptedData) {
    throw new Error("Decryption failed.");
  }

  return decryptedData;
};

/**
 * Calculate the CRC-16 checksum for given data.
 */
const calculateCRC16 = (data) => {
  let crc = 0xffff; // Initial CRC value
  for (let i = 0; i < data.sigBytes; i++) {
    crc ^= (data.words[i >>> 2] >>> ((3 - (i % 4)) << 3)) & 0xff;
    for (let j = 0; j < 8; j++) {
      if (crc & 0x0001) {
        crc = (crc >>> 1) ^ 0xa001;
      } else {
        crc >>>= 1;
      }
    }
  }
  return crc & 0xffff;
};

/**
 * XOR the given data with the key and encode the result in Base64.
 */
const xorWithKey = (data, key) => {
  let transformedBytes = "";
  for (let i = 0; i < data.length; i++) {
    transformedBytes += String.fromCharCode(
      data.charCodeAt(i) ^ key.charCodeAt(i % key.length)
    );
  }
  return btoa(transformedBytes); // Encode in Base64
};

/**
 * Decode Base64-encoded data, XOR with key, and decrypt with CRC16.
 */
const decodeXorWithKey = (encodedData, key) => {
  let transformedBytes = atob(encodedData);
  let originalBytes = "";

  for (let i = 0; i < transformedBytes.length; i++) {
    originalBytes += String.fromCharCode(
      transformedBytes.charCodeAt(i) ^ key.charCodeAt(i % key.length)
    );
  }

  return decryptWithCRC16(originalBytes);
};

// Export functions for use in React components
export { encryptWithCRC16, decryptWithCRC16, xorWithKey, decodeXorWithKey };
